import type { MeResource } from "@sarva/api-client";

/**
 * The timezone every timestamp in the product is rendered in (doc 41 §6, and
 * `<DateTime>`'s (H) rule in doc 27 §3).
 *
 * ── This used to fetch a practice, and it was silently wrong ───────────────
 *
 * `/me` did not carry a timezone once, so this hook read one from the full
 * practice resource and, when no practice was active, fell back to the
 * **browser's** zone. Its own docstring said "when `/me` grows a tenant
 * timezone this becomes one line". `/me` grew it — `TenantSummary.timezone` and
 * `PracticeSummary.timezone` are both on the resource now — and the hook did
 * not, so a tenant-wide identity with no practice selected (an owner, an
 * auditor, anyone viewing "All practices") read every timestamp in the product
 * in whatever zone their laptop was set to.
 *
 * Found in the browser, not by a test, and the reason no test caught it is
 * worth keeping: every route test seeds an active practice, so the practice
 * branch always ran — and in the one path that did not, the test runner's zone
 * happened to be the tenant's. **A wrong timezone on a filing deadline is a
 * missed deadline**, and it is wrong by hours in the direction that matters:
 * a deadline that reads as tomorrow is today.
 *
 * ── The order, and why the browser is still last ───────────────────────────
 *
 * The **practice** first: a claim's deadlines are computed in the practice's
 * zone, and a tenant spanning zones has practices that genuinely differ. Then
 * the **tenant**, which is the right answer for a tenant-wide view. The browser
 * only where the API told us nothing, and never a hardcoded region — a literal
 * `"America/New_York"` is wrong for three quarters of the country and silently.
 */
export function sessionTimeZone(me: MeResource | undefined): string {
  return (
    me?.active_practice?.timezone ?? me?.tenant.timezone ?? browserTimeZone()
  );
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // `Intl` without a resolvable zone is possible in a stripped runtime. UTC
    // is the only defensible default: it is never silently *nearly* right.
    return "UTC";
  }
}
